// src/components/content-preview-tray.tsx
import { useEffect } from "react";
import {
  X,
  FileText,
  Target,
  MapPin,
  Wrench,
  BookOpen,
  ExternalLink,
  TrendingUp,
  Zap,
  Shield,
  Tag,
  Calendar,
  Database,
} from "lucide-react";
import type { SimulationQueueItem } from "@/app/services/simulation/ai-sandbox-service";

interface ContentPreviewTrayProps {
  item: SimulationQueueItem;
  onClose: () => void;
}

/* ---------- helpers ---------- */

const PRIORITY_STYLE: Record<string, { color: string; bg: string; label: string }> = {
  HIGH:   { color: "var(--infrared-coral)", bg: "var(--infrared-coral-5)", label: "High priority" },
  MEDIUM: { color: "var(--chart-4)",        bg: "var(--chart-4-5)",         label: "Medium priority" },
  LOW:    { color: "var(--signal-blue)",    bg: "var(--signal-blue-8)",     label: "Low priority" },
};

function SectionHeader({
  icon: Icon,
  label,
  color,
  count,
}: {
  icon: React.ElementType;
  label: string;
  color: string;
  count?: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)` }}
      >
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <span
        style={{
          fontSize: "12px",
          fontWeight: 700,
          color,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      {typeof count === "number" && count > 0 && (
        <span
          className="px-1.5 py-0.5 rounded-full"
          style={{
            backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
            color,
            fontSize: "10px",
            fontWeight: 700,
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function MetaChip({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) {
  if (!value) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 flex items-center gap-2"
      style={{
        backgroundColor: "var(--muted-30)",
        border: "1px solid var(--border)",
        minWidth: "120px",
      }}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
      <div className="min-w-0">
        <p
          style={{
            fontSize: "10px",
            color: "var(--muted-foreground)",
            margin: 0,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontWeight: 600,
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--foreground)",
            margin: 0,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={value}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function NumberedBulletList({ items, color }: { items: string[]; color: string }) {
  return (
    <ol className="space-y-2" style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
      {items.map((s, i) => (
        <li
          key={i}
          className="flex items-start gap-3 rounded px-3 py-2"
          style={{
            backgroundColor: "var(--background)",
            border: "1px solid var(--border)",
          }}
        >
          <span
            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
              color,
              fontSize: "11px",
              fontWeight: 700,
              marginTop: "1px",
            }}
          >
            {i + 1}
          </span>
          <span
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              lineHeight: 1.6,
              flex: 1,
            }}
          >
            {s}
          </span>
        </li>
      ))}
    </ol>
  );
}

/* ---------- main ---------- */

export function ContentPreviewTray({ item, onClose }: ContentPreviewTrayProps) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const title = String(item.title || "Untitled action");
  const description = String(item.description || item.summary || "").trim();
  const sourceIssue = String((item as any).sourceIssue || "").trim();
  const whereToUpdate = (((item as any).whereToUpdate as string[]) || [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  const updateDetails = (((item as any).updateDetails as string[]) || [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  const references = (((item as any).references as any[]) || []).filter(
    (r) => r && (r.label || r.excerpt || r.url)
  );

  const priorityKey = String(item.priority || "MEDIUM").toUpperCase();
  const priorityCfg = PRIORITY_STYLE[priorityKey] || PRIORITY_STYLE.MEDIUM;

  const impactNum =
    typeof item.impact === "number"
      ? item.impact
      : parseInt(String(item.impact ?? item.expectedImpact ?? ""), 10);
  const hasImpact = Number.isFinite(impactNum) && impactNum > 0;

  const confidenceNum =
    typeof item.confidence === "number"
      ? item.confidence
      : parseInt(String(item.confidence ?? ""), 10);
  const hasConfidence = Number.isFinite(confidenceNum) && confidenceNum > 0;

  const effortStr = String(item.effort || "").trim();
  const sourceStr = String(item.source || "").trim();
  const typeStr = String(item.type || "").trim();
  const dateStr = item.lastModified
    ? new Date(item.lastModified).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-title"
        className="fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl"
        style={{
          width: "min(720px, 100vw)",
          backgroundColor: "var(--background)",
          borderLeft: "1px solid var(--border)",
          animation: "slideInRight 0.2s ease-out",
        }}
      >
        {/* ── Sticky header ──────────────────────────────────────── */}
        <div
          className="flex-shrink-0 px-6 py-4 border-b"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--background)",
          }}
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{
                    backgroundColor: priorityCfg.bg,
                    color: priorityCfg.color,
                  }}
                >
                  {priorityCfg.label}
                </span>
                {typeStr && (
                  <span
                    style={{
                      fontSize: "11px",
                      color: "var(--muted-foreground)",
                      fontWeight: 500,
                    }}
                  >
                    {typeStr}
                  </span>
                )}
              </div>
              <h3
                id="preview-title"
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: 700,
                  color: "var(--foreground)",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--muted-30)] transition-colors"
              style={{ color: "var(--muted-foreground)" }}
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Meta chip row */}
          <div className="flex items-center gap-2 flex-wrap">
            {hasImpact && (
              <MetaChip
                icon={TrendingUp}
                label="Impact"
                value={`${impactNum}/100`}
                color="var(--alpha-teal)"
              />
            )}
            {effortStr && (
              <MetaChip
                icon={Zap}
                label="Effort"
                value={effortStr}
                color="var(--chart-4)"
              />
            )}
            {hasConfidence && (
              <MetaChip
                icon={Shield}
                label="Confidence"
                value={`${confidenceNum}%`}
                color="var(--signal-blue)"
              />
            )}
            {sourceStr && (
              <MetaChip
                icon={Database}
                label="Source"
                value={sourceStr}
                color="var(--spectral-violet)"
              />
            )}
            {dateStr && (
              <MetaChip
                icon={Calendar}
                label="Last modified"
                value={dateStr}
                color="var(--muted-foreground)"
              />
            )}
          </div>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Description */}
          {description && (
            <section>
              <SectionHeader icon={FileText} label="Description" color="var(--foreground)" />
              <div
                className="rounded-lg px-4 py-3"
                style={{
                  backgroundColor: "var(--muted-30)",
                  border: "1px solid var(--border)",
                }}
              >
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    lineHeight: 1.7,
                    margin: 0,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {description}
                </p>
              </div>
            </section>
          )}

          {/* Source issue */}
          {sourceIssue && (
            <section>
              <SectionHeader icon={Target} label="Source issue" color="var(--infrared-coral)" />
              <div
                className="rounded-lg px-4 py-3"
                style={{
                  backgroundColor: "var(--infrared-coral-5)",
                  border: "1px solid var(--infrared-coral-20)",
                }}
              >
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    lineHeight: 1.7,
                    margin: 0,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {sourceIssue}
                </p>
              </div>
            </section>
          )}

          {/* Where to update */}
          {whereToUpdate.length > 0 && (
            <section>
              <SectionHeader
                icon={MapPin}
                label="Where to update"
                color="var(--signal-blue)"
                count={whereToUpdate.length}
              />
              <NumberedBulletList items={whereToUpdate} color="var(--signal-blue)" />
            </section>
          )}

          {/* What needs to change */}
          {updateDetails.length > 0 && (
            <section>
              <SectionHeader
                icon={Wrench}
                label="What needs to change"
                color="var(--alpha-teal)"
                count={updateDetails.length}
              />
              <NumberedBulletList items={updateDetails} color="var(--alpha-teal)" />
            </section>
          )}

          {/* References */}
          {references.length > 0 && (
            <section>
              <SectionHeader
                icon={BookOpen}
                label="References"
                color="var(--spectral-violet)"
                count={references.length}
              />
              <div className="space-y-3">
                {references.map((ref, i) => {
                  const label = String(ref.label || "Reference");
                  const excerpt = String(ref.excerpt || "").trim();
                  const url = ref.url ? String(ref.url).trim() : "";
                  return (
                    <div
                      key={i}
                      className="rounded-lg px-4 py-3"
                      style={{
                        backgroundColor: "var(--background)",
                        border: "1px solid var(--spectral-violet-12)",
                      }}
                    >
                      <div className="flex items-start gap-2 mb-1">
                        <Tag
                          className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                          style={{ color: "var(--spectral-violet)" }}
                        />
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:underline min-w-0"
                            style={{
                              fontSize: "var(--text-sm)",
                              fontWeight: 600,
                              color: "var(--spectral-violet)",
                              wordBreak: "break-word",
                            }}
                          >
                            <span>{label}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        ) : (
                          <span
                            style={{
                              fontSize: "var(--text-sm)",
                              fontWeight: 600,
                              color: "var(--foreground)",
                              wordBreak: "break-word",
                            }}
                          >
                            {label}
                          </span>
                        )}
                      </div>
                      {url && (
                        <p
                          style={{
                            fontSize: "11px",
                            color: "var(--muted-foreground)",
                            margin: 0,
                            marginLeft: "22px",
                            marginBottom: excerpt ? "6px" : 0,
                            wordBreak: "break-all",
                          }}
                        >
                          {url}
                        </p>
                      )}
                      {excerpt && (
                        <p
                          className="rounded px-3 py-2"
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--foreground)",
                            lineHeight: 1.6,
                            margin: 0,
                            marginLeft: "22px",
                            backgroundColor: "var(--muted-30)",
                            borderLeft: "2px solid var(--spectral-violet)",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {excerpt}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Empty state if the item is genuinely empty */}
          {!description &&
            !sourceIssue &&
            whereToUpdate.length === 0 &&
            updateDetails.length === 0 &&
            references.length === 0 && (
              <div
                className="rounded-lg px-6 py-12 text-center"
                style={{
                  backgroundColor: "var(--muted-30)",
                  border: "1px dashed var(--border)",
                }}
              >
                <FileText
                  className="w-8 h-8 mx-auto mb-3"
                  style={{ color: "var(--muted-foreground)" }}
                />
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    margin: 0,
                  }}
                >
                  No additional detail available for this action.
                </p>
              </div>
            )}
        </div>

        {/* ── Sticky footer ──────────────────────────────────────── */}
        <div
          className="flex-shrink-0 px-6 py-3 border-t flex items-center justify-between"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--muted-30)",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              color: "var(--muted-foreground)",
            }}
          >
            Press{" "}
            <kbd
              className="px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: "var(--background)",
                border: "1px solid var(--border)",
                fontSize: "10px",
                fontFamily: "monospace",
              }}
            >
              Esc
            </kbd>{" "}
            to close
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg transition-colors"
            style={{
              backgroundColor: "var(--background)",
              border: "1px solid var(--border)",
              color: "var(--foreground)",
              fontSize: "var(--text-sm)",
              fontWeight: 500,
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}